import React from 'react';
import '../../styles/ModuleDetails.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { Settings, BarChart3, Smartphone, Factory, BrainCircuit, Handshake } from 'lucide-react';

const ModuleDetails: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="module-details">
      <section className="module-section">
        <div className="container">
          <div className="module-grid">
            {/* Management Center Module */}
            <div id="management-center" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <Settings className="w-12 h-12" />
                </div>
                <h2>{t('features.module1.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module1.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.management.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.management.features.operators')}</li>
                  <li>{t('modules.management.features.shifts')}</li>
                  <li>{t('modules.management.features.fleets')}</li>
                  <li>{t('modules.management.features.activities')}</li>
                  <li>{t('modules.management.features.maintenance')}</li>
                  <li>{t('modules.management.features.operations')}</li>
                </ul>
              </div>
            </div>

            {/* Status and Report Module */}
            <div id="status-report" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <BarChart3 className="w-12 h-12" />
                </div>
                <h2>{t('features.module2.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module2.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.status.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.status.features.equipment')}</li>
                  <li>{t('modules.status.features.availability')}</li>
                  <li>{t('modules.status.features.utilization')}</li>
                  <li>{t('modules.status.features.mtbf')}</li>
                  <li>{t('modules.status.features.worked')}</li>
                  <li>{t('modules.status.features.operational')}</li>
                  <li>{t('modules.status.features.maintenance')}</li>
                </ul>
              </div>
            </div>

            {/* Mobile Module */}
            <div id="mobile" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <Smartphone className="w-12 h-12" />
                </div>
                <h2>{t('features.module3.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module3.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.mobile.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.mobile.features.integrated')}</li>
                  <li>{t('modules.mobile.features.interaction')}</li>
                  <li>{t('modules.mobile.features.realtime')}</li>
                  <li>{t('modules.mobile.features.offline')}</li>
                  <li>{t('modules.mobile.features.gps')}</li>
                  <li>{t('modules.mobile.features.intuitive')}</li>
                </ul>
              </div>
            </div>

            {/* Production Module */}
            <div id="production" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <Factory className="w-12 h-12" />
                </div>
                <h2>{t('features.module4.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module4.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.production.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.production.features.cycle')}</li>
                  <li>{t('modules.production.features.assignment')}</li>
                  <li>{t('modules.production.features.kpis')}</li>
                  <li>{t('modules.production.features.queue')}</li>
                  <li>{t('modules.production.features.tracking')}</li>
                  <li>{t('modules.production.features.realtime')}</li>
                </ul>
              </div>
            </div>

            {/* AI Analytics Module */}
            <div id="ai-analytics" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <BrainCircuit className="w-12 h-12" />
                </div>
                <h2>{t('features.module5.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module5.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.aianalytics.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.aianalytics.features.maintenance')}</li>
                  <li>{t('modules.aianalytics.features.bottleneck')}</li>
                  <li>{t('modules.aianalytics.features.forecast')}</li>
                  <li>{t('modules.aianalytics.features.anomaly')}</li>
                  <li>{t('modules.aianalytics.features.recommendation')}</li>
                  <li>{t('modules.aianalytics.features.learning')}</li>
                </ul>
              </div>
            </div>

            {/* Contract Management Module */}
            <div id="contract-management" className="module-card">
              <div className="module-header">
                <div className="module-icon">
                  <Handshake className="w-12 h-12" />
                </div>
                <h2>{t('features.module6.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module6.description')}
                </p>
              </div>

              <div className="module-features">
                <h3>{t('modules.contract.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.contract.features.subcontractors')}</li>
                  <li>{t('modules.contract.features.equipment')}</li>
                  <li>{t('modules.contract.features.billing')}</li>
                  <li>{t('modules.contract.features.performance')}</li>
                  <li>{t('modules.contract.features.expiry')}</li>
                  <li>{t('modules.contract.features.reporting')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Interfaces Section */}
      <section className="module-section">
        <div className="container">
          <div className="production-interfaces-section">
            <div className="production-gallery">
              <h4>{t('modules.production.gallery.title')}</h4>

              {/* Production Flow Explanation */}
              <div className="production-flow-section">
                <h5>{t('modules.production.flow.title')}</h5>
                <div className="flow-states">
                  <div className="flow-state">
                    <span className="state-badge state-empty">{t('modules.production.flow.empty')}</span>
                    <p>{t('modules.production.flow.empty.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-loading">{t('modules.production.flow.loading')}</span>
                    <p>{t('modules.production.flow.loading.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-full">{t('modules.production.flow.full')}</span>
                    <p>{t('modules.production.flow.full.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-dumping">{t('modules.production.flow.dumping')}</span>
                    <p>{t('modules.production.flow.dumping.desc')}</p>
                  </div>
                </div>
              </div>

              <div className="production-interfaces">
                <div className="interface-item">
                  <div className="interface-description">
                    <h6>{t('modules.production.excavator.title')}</h6>
                    <p>{t('modules.production.excavator.desc')}</p>
                  </div>
                </div>

                <div className="interface-item">
                  <div className="interface-description">
                    <h6>{t('modules.production.truck.title')}</h6>
                    <p>{t('modules.production.truck.desc')}</p>
                  </div>
                </div>

                {/* KPI Monitoring Section */}
                <div className="kpi-monitoring-section">
                  <h5>{t('modules.production.kpi.title')}</h5>
                  <p className="kpi-emphasis">{t('modules.production.kpi.emphasis')}</p>

                  <div className="kpi-categories">
                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">🚛</span>
                        <h6>{t('modules.production.kpi.truck.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.empty.travel')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.empty.travel.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.full.travel')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.full.travel.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.empty.distance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.empty.distance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.full.distance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.full.distance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.cycle.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.cycle.time.desc')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">⏱️</span>
                        <h6>{t('modules.production.kpi.operations.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.spot.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.spot.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.queue.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.queue.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.loading.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.loading.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.dumping.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.dumping.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.hang.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.hang.time.desc')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">📊</span>
                        <h6>{t('modules.production.kpi.analytics.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.total.production')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.total.production.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.equipment.utilization')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.equipment.utilization.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.cycle.efficiency')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.cycle.efficiency.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.fleet.performance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.fleet.performance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.realtime.monitoring')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.realtime.monitoring.desc')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModuleDetails;
